/* eslint-disable no-alert */
/* eslint-disable react-native/no-inline-styles */
import {useNavigation} from '@react-navigation/native';
import {
  Icon,
  IndexPath,
  Input,
  Layout,
  Select,
  SelectItem,
  Text,
} from '@ui-kitten/components';
import React, {useEffect, useState} from 'react';
import {Alert, Platform, StyleSheet} from 'react-native';
import {showMessage} from 'react-native-flash-message';
import {useDispatch, useSelector} from 'react-redux';
import {
  postChangePassword,
  postEditProfile,
  postUserDeleteAccount,
} from '../../apis';
import {
  AUTHENTICATED_ROUTES,
  UN_AUTHENTICATED_ROUTES,
} from '../../navigation/constants';
import {getAndSetAllKids} from '../../redux/actions';
import {getAndSetProfile, signOut} from '../../redux/actions/auth';
import {px, useAsyncFn, useAsyncRetry, useIsLandscape, wp} from '../../utils';
import {fieldUtils} from '../../utils/fieldUtils';
import {
  AppLoadingIndicator,
  LoadingButton,
  Page,
  TitleRow,
} from '../components';
import StudentImage from './StudentImage';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import moment from 'moment';

const schoolTypes = [
  {title: 'SK', id: 'SK'},
  {title: 'SJK(T)', id: 'SJK(T)'},
  {title: 'SJK(C)', id: 'SJK(C)'},
  {title: 'SMK', id: 'SMK'},
  {title: 'SMJK(C)', id: 'SMJK(C)'},
  {title: 'Private School', id: 'Private School'},
];

const shieldIcon = (props) => <Icon {...props} name="shield-outline" />;
const personIcon = (props) => <Icon {...props} name="person-outline" />;
const phoneIcon = (props) => <Icon {...props} name="phone-outline" />;
const lockIcon = (props) => <Icon {...props} name="lock-outline" />;

const initialFieldsErrors = {
  fullName: undefined,
  phone: undefined,
  mom_contact: undefined,
  dad_contact: undefined,
  schoolName: undefined,
  oldPassword: undefined,
  password: undefined,
  passwordConfirm: undefined,
};

const Settings = () => {
  const dispatch = useDispatch();
  const navigation = useNavigation();
  const isLandscape = useIsLandscape();
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [dad_contact, setDadContact] = useState('');
  const [mom_contact, setMomContact] = useState('');
  const [schoolType, setSchoolType] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [gradeIndex, setGradeIndex] = useState(new IndexPath(0));
  const [oldPassword, setOldPassword] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [fieldsErrors, setFieldsErrors] = useState({...initialFieldsErrors});
  const user = useSelector((state) => state.auth.user);
  const grades = useSelector((state) => state.general.grades);

  const gradeObj = grades.find((_, rowIndex) => rowIndex === gradeIndex.row);

  const {value: kids, retry: handleGetKids} = useAsyncRetry(
    () => dispatch(getAndSetAllKids()),
    [],
  );

  var kidsImage = '';
  if (kids && kids.length > 0) {
    kidsImage = kids[0].kidsImage;
  }

  const {loading: loadingProfile, retry: handleGetProfile} = useAsyncRetry(
    () => dispatch(getAndSetProfile()),
    [],
  );

  const [
    {loading: loadingEditProfile},
    handleEditProfile,
  ] = useAsyncFn(async () => {
    if (!isProfileFormValid()) {
      return;
    }

    await postEditProfile({
      contactNumber: phone,
      fullName,
      momContact: mom_contact,
      dadContact: dad_contact,
      schoolName,
      schoolType: schoolTypes[schoolType.row]?.id,
      gradeId: gradeObj.id,
    });
    showMessage({
      message: 'Profile update successfully!',
      type: 'success',
      autoHide: true,
      duration: 3000,
      position: 'top right',
    });
    handleGetKids();
    handleGetProfile();
  }, [phone, fullName, mom_contact, dad_contact, schoolName, gradeObj]);

  useEffect(() => {
    if (user) {
      setFullName(user.fullName || '');
      setPhone(user.contactNumber || '');
      setMomContact(user.momContact || '');
      setDadContact(user.dadContact || '');
      setSchoolName(user.schoolName || '');

      const typeIndexToSet = schoolTypes.findIndex(
        (item) => item.id === user?.schoolType,
      );

      setSchoolType(new IndexPath(typeIndexToSet > -1 ? typeIndexToSet : 0));

      const gradeIndexToSet = grades.findIndex(
        (gradeRow) => gradeRow.id === user?.gradeId,
      );
      setGradeIndex(new IndexPath(gradeIndexToSet > -1 ? gradeIndexToSet : 0));
    }
  }, [grades, user]);

  const isProfileFormValid = () => {
    const requiredErrors = checkProfileRequiredFieldsError();
    if (Object.keys(requiredErrors).length) {
      setFieldsErrors(requiredErrors);
      return false;
    }

    setFieldsErrors(initialFieldsErrors);
    return true;
  };

  const checkProfileRequiredFieldsError = () => {
    const errorText = 'This field should be filled out.';
    const errors = {};

    if (fullName === '') {
      errors.fullName = errorText;
    }
    if (phone === '') {
      errors.phone = errorText;
    }
    if (mom_contact === '') {
      errors.mom_contact = errorText;
    }
    if (dad_contact === '') {
      errors.dad_contact = errorText;
    }
    if (schoolName === '') {
      errors.schoolName = errorText;
    }

    return errors;
  };

  const deleteConfirmation = () => {
    Alert.prompt(
      'Confirmation',
      'Are you sure to delete your account? Once deleted they cannot be recovered\n\nPlease write "delete" to confirm',
      [
        {
          text: 'Cancel',
          onPress: () => {},
        },
        {
          text: 'OK',
          onPress: (text) => {
            if (text === 'delete') {
              handleDeleteAccount();
            } else {
              Alert.alert('Oops', 'Please write "delete" to confirm');
            }
          },
        },
      ],
    );
  };

  const [{deleteLoading}, handleDeleteAccount] = useAsyncFn(async () => {
    try {
      await postUserDeleteAccount();
      dispatch(signOut());
      Alert.alert('Done', 'Account deleted successfully!');
      navigation.navigate(UN_AUTHENTICATED_ROUTES.welcomeScreen.path);
    } catch (err) {
      Alert.alert('Error', 'Something went wrong!');
    }
  }, [password, passwordConfirm, oldPassword]);

  const [{loading}, handleChangePassword] = useAsyncFn(async () => {
    try {
      if (!isPasswordFormValid()) {
        return;
      }
      await postChangePassword({newPassword: password, oldPassword});
      alert('Password change successfully!');
      navigation.navigate(AUTHENTICATED_ROUTES.main.path);
    } catch (err) {
      alert('Your old password is wrong!');
    }
  }, [password, passwordConfirm, oldPassword]);

  // const onLogout = () => {
  //   setTimeout(() => {
  //     dispatch(signOut());
  //   }, 500);
  //   navigation.navigate(UN_AUTHENTICATED_ROUTES.signIn.path);
  // };

  const checkRequiredFieldsError = () => {
    const errorText = 'This field should be filled out.';
    const errors = {};

    if (oldPassword === '') {
      errors.oldPassword = errorText;
    }

    if (password === '') {
      errors.password = errorText;
    }

    if (passwordConfirm === '') {
      errors.passwordConfirm = errorText;
    }

    return errors;
  };

  const checkPasswordsError = () => {
    const regexErrorText =
      'Password should not contain space and should be min 6 characters!';
    // no whitespace and min 6 char
    const passRegex = /^\S{6,}$/;
    const errors = {};

    if (password === passwordConfirm) {
      if (!password.match(passRegex)) {
        errors.password = regexErrorText;
        errors.passwordConfirm = regexErrorText;
      }
    } else {
      const text = 'Both Password do not match!';
      errors.password = text;
      errors.passwordConfirm = text;
    }

    return errors;
  };

  const isPasswordFormValid = () => {
    const requiredErrors = checkRequiredFieldsError();
    if (Object.keys(requiredErrors).length) {
      setFieldsErrors(requiredErrors);
      return false;
    }

    const passwordErrors = checkPasswordsError();
    if (Object.keys(passwordErrors).length) {
      setFieldsErrors(passwordErrors);
      return false;
    }

    setFieldsErrors({});
    return true;
  };

  // const handleUpdateChanges = () => {
  //   if (password) {
  //     if (!isPasswordFormValid()) {
  //       return;
  //     }
  //     handleChangePassword();
  //   }
  //   handleEditProfile();
  // };

  const todayDate = moment().unix();
  const futureDate = moment('2023-02-15').unix();
  const isDeleteVisible = todayDate < futureDate;

  return (
    <KeyboardAwareScrollView style={styles.keyboardContainer}>
      <Page
        header={{title: 'Settings', showDrawerAction: true}}
        scrollViewStyle={[
          isLandscape ? {paddingHorizontal: wp(20)} : undefined,
        ]}>
        {loadingProfile ? (
          <AppLoadingIndicator />
        ) : (
          <Layout style={styles.container}>
            <Layout style={styles.avatarContainer}>
              <StudentImage kidsImage={kidsImage} />
            </Layout>

            <Input
              value={fullName}
              onChangeText={setFullName}
              placeholder="Full Name"
              accessoryLeft={personIcon}
              style={styles.input}
              status={fieldUtils.getFieldStatus(fieldsErrors.fullName)}
              caption={fieldUtils.getErrorText(fieldsErrors.fullName)}
              captionIcon={fieldUtils.getErrorIcon(fieldsErrors.fullName)}
            />

            <Input
              label={(evaProps) => <Text {...evaProps}>Contact Number : </Text>}
              value={phone}
              onChangeText={setPhone}
              placeholder="Phone"
              accessoryLeft={phoneIcon}
              style={styles.input}
              status={fieldUtils.getFieldStatus(fieldsErrors.phone)}
              caption={fieldUtils.getErrorText(fieldsErrors.phone)}
              captionIcon={fieldUtils.getErrorIcon(fieldsErrors.phone)}
            />

            <Input
              label={(evaProps) => (
                <Text {...evaProps}>DAD Contact Number : </Text>
              )}
              value={dad_contact}
              onChangeText={setDadContact}
              placeholder="Dad Contact Number"
              accessoryLeft={phoneIcon}
              style={styles.input}
              status={fieldUtils.getFieldStatus(fieldsErrors.dad_contact)}
              caption={fieldUtils.getErrorText(fieldsErrors.dad_contact)}
              captionIcon={fieldUtils.getErrorIcon(fieldsErrors.dad_contact)}
            />

            <Input
              label={(evaProps) => (
                <Text {...evaProps}>MOM Contact Number : </Text>
              )}
              value={mom_contact}
              onChangeText={setMomContact}
              placeholder="Mom Contact Number"
              accessoryLeft={phoneIcon}
              style={styles.input}
              status={fieldUtils.getFieldStatus(fieldsErrors.mom_contact)}
              caption={fieldUtils.getErrorText(fieldsErrors.mom_contact)}
              captionIcon={fieldUtils.getErrorIcon(fieldsErrors.mom_contact)}
            />

            <Select
              label={(evaProps) => <Text {...evaProps}>School name: </Text>}
              selectedIndex={schoolType}
              value={schoolTypes[schoolType.row]?.id || ''}
              status={fieldUtils.getFieldStatus(fieldsErrors.schoolType)}
              caption={fieldUtils.getErrorText(fieldsErrors.schoolType)}
              captionIcon={fieldUtils.getErrorIcon(fieldsErrors.schoolType)}
              onSelect={setSchoolType}>
              {schoolTypes.map((item, index) => {
                return <SelectItem title={item.title} key={index} />;
              })}
            </Select>

            <Input
              style={styles.input}
              placeholder="Enter School Name"
              value={schoolName}
              onChangeText={setSchoolName}
              status={fieldUtils.getFieldStatus(fieldsErrors.schoolName)}
              caption={fieldUtils.getErrorText(fieldsErrors.schoolName)}
              captionIcon={fieldUtils.getErrorIcon(fieldsErrors.schoolName)}
            />

            <Select
              label={(evaProps) => (
                <Text {...evaProps}>Select Your Grade : </Text>
              )}
              accessoryLeft={shieldIcon}
              value={gradeObj.type}
              selectedIndex={gradeIndex}
              onSelect={setGradeIndex}>
              {grades.map(({type}, itemIndex) => (
                <SelectItem title={type} key={itemIndex} />
              ))}
            </Select>

            <LoadingButton
              appearance="primary"
              loading={loadingEditProfile}
              style={styles.marginTop}
              onPress={() => handleEditProfile()}>
              {'Update Profile'}
            </LoadingButton>
          </Layout>
        )}
        <Layout style={styles.signOut}>
          <TitleRow
            type="secondary"
            title="Change password"
            textStyle={{fontWeight: '600', fontSize: 18}}
            containerStyle={{paddingHorizontal: 0}}
          />
          <Input
            value={oldPassword}
            placeholder="Old password"
            accessoryLeft={lockIcon}
            onChangeText={setOldPassword}
          />
          <Input
            value={password}
            placeholder="New password"
            accessoryLeft={lockIcon}
            onChangeText={setPassword}
            style={styles.marginTop}
            status={fieldUtils.getFieldStatus(fieldsErrors.password)}
            caption={fieldUtils.getErrorText(fieldsErrors.password)}
            captionIcon={fieldUtils.getErrorIcon(fieldsErrors.password)}
          />
          <Input
            value={passwordConfirm}
            placeholder="Confirm password"
            accessoryLeft={lockIcon}
            onChangeText={setPasswordConfirm}
            style={styles.marginTop}
            status={fieldUtils.getFieldStatus(fieldsErrors.passwordConfirm)}
            caption={fieldUtils.getErrorText(fieldsErrors.passwordConfirm)}
            captionIcon={fieldUtils.getErrorIcon(fieldsErrors.passwordConfirm)}
          />
          <LoadingButton
            appearance="primary"
            loading={loading}
            style={styles.marginTop}
            onPress={() => handleChangePassword()}>
            {'Update Password'}
          </LoadingButton>

          {Platform.OS === 'ios' && isDeleteVisible && (
            <LoadingButton
              onPress={deleteConfirmation}
              loading={deleteLoading}
              style={styles.marginTop}
              status="danger">
              {'Delete Account'}
            </LoadingButton>
          )}
        </Layout>
      </Page>
    </KeyboardAwareScrollView>
  );
};

const styles = StyleSheet.create({
  keyboardContainer: {
    flex: 1,
    backgroundColor: '#FFF',
  },
  signOut: {
    paddingHorizontal: px(17),
    marginTop: px(28),
    marginBottom: px(28),
  },
  marginTop: {
    marginTop: px(17),
  },
  container: {
    paddingHorizontal: px(17),
    paddingBottom: 20,
  },
  input: {
    marginBottom: px(17),
  },
  avatarContainer: {
    alignItems: 'center',
    marginTop: px(40),
    marginBottom: px(28),
  },
});

export default Settings;
